import { useQuery } from '@apollo/client';
import type { ColDef } from 'ag-grid-community';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ListPageLayout } from '@/components/layout/list-page-layout';
import { openEntityInNewWindow } from '@/lib/open-in-new-window';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SearchInput } from '@/components/ui/search-input';
import { SentryDropdown } from '@/components/ui/sentry-dropdown';
import { CancelReason, ParkingSessionStatus } from '@/graphql/generated';

import { useDebounce } from '@/hooks/useDebounce';
import { useGo } from '@/hooks/useResourceNavigation';
import { ParkingSquare, LogOut, List, Ban } from 'lucide-react';

import { ParkingSessionDetailsQuery } from './parking-session-details-query';
import { PARKING_SESSIONS_LIST_QUERY } from './parking-sessions.gql';
import { ParkingSessionStatusBadge } from './status-badge';
import { ParkingSessionListItem } from './types';
import {
	calculateDuration,
	formatCurrency,
	formatCustomerName,
	formatDateTime,
	formatDuration,
	getParkingSessionDisplayStatus,
	getCancelReasonKey
} from './utils';

interface ParkingSessionNode {
	parkingSessionId: string;
	ticketId?: string | null;
	entryDateTime: string;
	exitDateTime?: string | null;
	paymentDateTime?: string | null;
	paymentAmount?: number | null;
	vehicleId?: string | null;
	parkingSessionTypeId?: number | null;
	paymentMethodId?: number | null;
	customerId?: string | null;
	parkingPositionId?: string | null;
	contractId?: string | null;
	status: ParkingSessionStatus;
	cancelReason?: CancelReason | null;
	vehicle?: {
		vehicleId: string;
		plateNumber?: string | null;
		make?: string | null;
		model?: string | null;
		color?: string | null;
		vehicleType?: {
			name?: string | null;
		} | null;
	} | null;
	parkingSessionType?: {
		parkingSessionTypeId: number;
		name?: string | null;
	} | null;
	paymentMethod?: {
		paymentMethodId: number;
		name?: string | null;
	} | null;
	customer?: {
		customerId: string;
		firmName?: string | null;
		firstName?: string | null;
		lastName?: string | null;
		isCompany?: boolean | null;
	} | null;
	parkingPosition?: {
		parkingPositionId: string;
		code?: string | null;
	} | null;
	contract?: {
		contractId: string;
		beginDate?: string | null;
		endDate?: string | null;
	} | null;
}

interface ParkingSessionsListQueryResponse {
	parkingSessions?: {
		nodes?: ParkingSessionNode[] | null;
		pageInfo?: {
			hasNextPage: boolean;
			endCursor?: string | null;
		} | null;
	} | null;
}

const PARKING_SESSIONS_PAGE_SIZE = 100;

const ParkingSessionEntityIcon = ({ status }: { status: ParkingSessionStatus }) => {
	const getIconStyles = () => {
		switch (status) {
			case ParkingSessionStatus.Inside:
				return {
					borderClass: 'border-blue-400 dark:border-blue-500',
					iconClass: 'text-blue-600 dark:text-blue-400',
					Icon: ParkingSquare
				};
			case ParkingSessionStatus.Canceled:
				return {
					borderClass: 'border-red-400 dark:border-red-500',
					iconClass: 'text-red-600 dark:text-red-400',
					Icon: Ban
				};
			case ParkingSessionStatus.Exited:
			default:
				return {
					borderClass: 'border-gray-400 dark:border-gray-500',
					iconClass: 'text-gray-500 dark:text-gray-400',
					Icon: LogOut
				};
		}
	};

	const { borderClass, iconClass, Icon } = getIconStyles();

	return (
		<div className={`flex items-center justify-center w-14 h-14 rounded-full bg-transparent border-2 flex-shrink-0 ${borderClass}`}>
			<Icon className={`w-7 h-7 ${iconClass}`} />
		</div>
	);
};

const mapNodeToListItem = (
	node: ParkingSessionNode
): ParkingSessionListItem => {
	const durationMinutes = calculateDuration(
		node.entryDateTime,
		node.exitDateTime
	);

	const displayStatus = getParkingSessionDisplayStatus(node.status);

	return {
		parkingSessionId: node.parkingSessionId,
		ticketId: node.ticketId,
		entryDateTime: node.entryDateTime,
		exitDateTime: node.exitDateTime,
		paymentDateTime: node.paymentDateTime,
		paymentAmount: node.paymentAmount,
		vehicleId: node.vehicleId,
		vehiclePlateNumber: node.vehicle?.plateNumber ?? 'Unknown Vehicle',
		vehicleMake: node.vehicle?.make ?? undefined,
		vehicleModel: node.vehicle?.model ?? undefined,
		vehicleColor: node.vehicle?.color ?? undefined,
		vehicleTypeName: node.vehicle?.vehicleType?.name ?? undefined,
		parkingSessionType: node.parkingSessionType?.name ?? undefined,
		paymentMethod: node.paymentMethod?.name ?? undefined,
		customerId: node.customerId,
		customerName: formatCustomerName(
			node.customer?.isCompany,
			node.customer?.firmName,
			node.customer?.firstName,
			node.customer?.lastName
		),
		parkingPositionCode: node.parkingPosition?.code ?? undefined,
		contractId: node.contractId,
		durationMinutes: durationMinutes ?? undefined,
		status: node.status,
		cancelReason: node.cancelReason,
		displayStatus
	};
};

export const ParkingSessionsList = () => {
	const { t } = useTranslation('parkingSessions');
	const { t: tPS } = useTranslation('parkingSessions');
	const go = useGo();

	const STATUS_ALL = '';

	const statusOptions = useMemo(
		() => [
			{ value: STATUS_ALL, label: tPS('list.filters.all'), icon: <List className="w-4 h-4 text-gray-500" /> },
			{ value: ParkingSessionStatus.Inside, label: tPS('list.filters.inside'), icon: <ParkingSquare className="w-4 h-4 text-blue-600 dark:text-blue-400" /> },
			{ value: ParkingSessionStatus.Exited, label: tPS('list.filters.exited'), icon: <LogOut className="w-4 h-4 text-gray-500 dark:text-gray-400" /> },
			{ value: ParkingSessionStatus.Canceled, label: tPS('list.filters.canceled'), icon: <Ban className="w-4 h-4 text-red-500 dark:text-red-400" /> }
		],
		[tPS]
	);

	const handleClearFilters = () => {
		setSearchText('');
		setSelectedDate('');
		setSelectedStatus(ParkingSessionStatus.Inside);
	};
	const [selectedStatus, setSelectedStatus] = useState<string>(ParkingSessionStatus.Inside);
	const [selectedDate, setSelectedDate] = useState<string>('');
	const [searchText, setSearchText] = useState<string>('');
	const debouncedSearchText = useDebounce(searchText, 500);
	const debouncedSelectedStatus = useDebounce(selectedStatus, 500);

	const hasActiveFilters = useMemo(() => {
		return (
			searchText !== '' ||
			selectedDate !== '' ||
			selectedStatus !== ParkingSessionStatus.Inside
		);
	}, [searchText, selectedDate, selectedStatus]);

	const queryVariables = useMemo(() => {
		const whereConditions: any[] = [];

		// Add date filter if selected
		if (selectedDate) {
			const startOfDay = new Date(selectedDate);
			startOfDay.setHours(0, 0, 0, 0);
			const endOfDay = new Date(selectedDate);
			endOfDay.setHours(23, 59, 59, 999);
			whereConditions.push({
				entryDateTime: {
					gte: startOfDay.toISOString(),
					lte: endOfDay.toISOString()
				}
			});
		}

		// Add search filter for both ticketId and vehicle plateNumber (debounced)
		if (debouncedSearchText) {
			whereConditions.push({
				or: [
					{
						ticketId: {
							contains: debouncedSearchText
						}
					},
					{
						vehicle: {
							plateNumber: {
								contains: debouncedSearchText
							}
						}
					}
				]
			});
		}

		// Add status filter using the new status enum (debounced to avoid rapid refetches)
		if (debouncedSelectedStatus && debouncedSelectedStatus !== STATUS_ALL) {
			whereConditions.push({
				status: {
					eq: debouncedSelectedStatus
				}
			});
		}

		// Combine conditions with AND logic
		if (whereConditions.length === 0) {
			return {};
		}
		if (whereConditions.length === 1) {
			return { where: whereConditions[0] };
		}
		return {
			where: {
				and: whereConditions
			}
		};
	}, [selectedDate, debouncedSearchText, debouncedSelectedStatus]);

	const { data, loading, error, refetch, fetchMore } =
		useQuery<ParkingSessionsListQueryResponse>(
			PARKING_SESSIONS_LIST_QUERY,
			{
				variables: {
					...queryVariables,
					first: PARKING_SESSIONS_PAGE_SIZE
				},
				skip: selectedStatus !== debouncedSelectedStatus,
				fetchPolicy: 'cache-and-network',
				errorPolicy: 'all'
			}
		);

	// Pagination state
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const hasNextPage = data?.parkingSessions?.pageInfo?.hasNextPage ?? false;
	const endCursor = data?.parkingSessions?.pageInfo?.endCursor;

	// Load more handler using fetchMore
	const handleLoadMore = useCallback(async () => {
		if (!hasNextPage || isLoadingMore || !endCursor) return;

		setIsLoadingMore(true);
		try {
			await fetchMore({
				variables: {
					...queryVariables,
					first: PARKING_SESSIONS_PAGE_SIZE,
					after: endCursor
				},
				updateQuery: (prev, { fetchMoreResult }) => {
					if (!fetchMoreResult?.parkingSessions) return prev;

					return {
						...prev,
						parkingSessions: {
							...fetchMoreResult.parkingSessions,
							nodes: [
								...(prev.parkingSessions?.nodes ?? []),
								...(fetchMoreResult.parkingSessions.nodes ?? [])
							]
						}
					};
				}
			});
		} catch (error) {
			console.error('Error loading more parking sessions:', error);
		} finally {
			setIsLoadingMore(false);
		}
	}, [fetchMore, queryVariables, hasNextPage, isLoadingMore, endCursor]);

	useEffect(() => {
		if (error) {
			console.warn('Parking sessions query failed:', error);
		}
	}, [error]);

	const parkingSessions = useMemo(() => {
		const nodes = data?.parkingSessions?.nodes ?? [];
		return nodes
			.filter((node): node is ParkingSessionNode => Boolean(node))
			.map(node => {
				try {
					return mapNodeToListItem(node);
				} catch (mappingError) {
					console.warn(
						'Unable to map parking session node',
						mappingError
					);
					return null;
				}
			})
			.filter((item): item is ParkingSessionListItem => item !== null);
	}, [data?.parkingSessions?.nodes]);

	// Local state to manage parking sessions list with stable data
	// This pattern ensures data continuity for AG Grid animations during filter changes
	const [localParkingSessions, setLocalParkingSessions] = useState<
		ParkingSessionListItem[]
	>([]);

	// Track if query is skipped (during debounce transition)
	// When skipped, Apollo sets data to undefined, so we need to preserve local state
	const isQuerySkipped = selectedStatus !== debouncedSelectedStatus;

	// Update local state only when query actually completes (not skipped, not loading)
	// This ensures we properly transition to empty state when filters return no results
	useEffect(() => {
		if (!loading && !isQuerySkipped) {
			setLocalParkingSessions(parkingSessions);
		}
	}, [loading, isQuerySkipped, parkingSessions]);

	// Use local state while loading OR skipped to maintain data continuity
	// This prevents the grid from resetting/flashing while new filtered data loads
	const filteredParkingSessions =
		(loading || isQuerySkipped) && localParkingSessions.length > 0
			? localParkingSessions
			: parkingSessions;

	const [selectedSession, setSelectedSession] =
		useState<ParkingSessionListItem | null>(
			filteredParkingSessions.length > 0
				? filteredParkingSessions[0]
				: null
		);

	useEffect(() => {
		if (filteredParkingSessions.length === 0) {
			setSelectedSession(null);
			return;
		}

		// If no selection, select first item
		if (!selectedSession) {
			setSelectedSession(filteredParkingSessions[0]);
			return;
		}

		// Check if currently selected session still exists in the filtered list
		const selectionExists = filteredParkingSessions.some(
			(session: ParkingSessionListItem) =>
				session.parkingSessionId === selectedSession.parkingSessionId
		);

		// Only update if selection no longer exists in the list
		if (!selectionExists) {
			setSelectedSession(filteredParkingSessions[0]);
		}
		// Note: We don't update selectedSession reference even if it exists
		// This prevents unnecessary re-renders and selection issues
	}, [filteredParkingSessions, selectedSession?.parkingSessionId]);

	const columns: ColDef<ParkingSessionListItem>[] = [
		{
			headerName: t('list.columns.plate'),
			field: 'vehiclePlateNumber',
			width: 120,
			pinned: 'left',
			cellClass: 'font-semibold'
		},
		{
			headerName: t('list.columns.ticket'),
			field: 'ticketId',
			width: 140,
			cellClass: 'font-semibold',
			valueGetter: params => params.data?.ticketId ?? '—'
		},
		{
			headerName: t('list.columns.entry'),
			field: 'entryDateTime',
			width: 160,
			valueGetter: params => formatDateTime(params.data?.entryDateTime),
			sort: 'desc'
		},
		{
			headerName: t('list.columns.duration'),
			field: 'durationMinutes',
			width: 100,
			valueGetter: params => formatDuration(params.data?.durationMinutes),
			cellClass: params =>
				params.data?.status === ParkingSessionStatus.Inside
					? 'text-blue-600 dark:text-blue-400 font-semibold'
					: ''
		},
		{
			headerName: t('list.columns.exit'),
			field: 'exitDateTime',
			width: 160,
			valueGetter: params => formatDateTime(params.data?.exitDateTime)
		},
		{
			headerName: t('list.columns.status'),
			field: 'displayStatus',
			width: 110,
			cellRenderer: (params: any) => {
				if (!params.data) return null;
				return (
					<ParkingSessionStatusBadge
						status={params.data.displayStatus}
					/>
				);
			}
		},
		{
			headerName: t('list.columns.amount'),
			field: 'paymentAmount',
			width: 110,
			valueGetter: params => formatCurrency(params.data?.paymentAmount)
		},
		{
			headerName: t('list.columns.customer'),
			field: 'customerName',
			flex: 1,
			minWidth: 150,
			valueGetter: params => params.data?.customerName ?? '—'
		},
		{
			headerName: t('list.columns.position'),
			field: 'parkingPositionCode',
			width: 100,
			valueGetter: params => params.data?.parkingPositionCode ?? '—'
		}
	];

	return (
		<>
			<ListPageLayout
				columns={columns}
				data={filteredParkingSessions}
				title={t('list.title')}
				description={t('list.description')}
				showSearch={false}
				isLoading={loading}
				onRowClick={row => setSelectedSession(row)}
				onOpenInNewWindow={id => openEntityInNewWindow('parkingSessions', id)}
				onRefresh={() => {
					void refetch();
				}}
				hasNextPage={hasNextPage}
				isLoadingMore={isLoadingMore}
				onLoadMore={handleLoadMore}
				entityName={t('entityName')}
				selectedItem={selectedSession}
				detailsTitle={t('view.sessionDetails')}
				renderDetailsContent={item => (
					<ParkingSessionDetailsQuery
						parkingSessionId={item.parkingSessionId}
					/>
				)}
				getRecordId={item => item.parkingSessionId}
				getDetailsDescription={item =>
					`${item.vehiclePlateNumber} · ${item.ticketId ?? ''}`
				}
				getHeaderSubtitle={item => (
					<div className="flex items-center gap-2 mt-1">
						<ParkingSessionStatusBadge
							status={item.displayStatus}
						/>
						{item.displayStatus === 'Canceled' && item.cancelReason && (
							<Badge variant="outline" className="text-red-600 dark:text-red-400 border-red-300 dark:border-red-700">
								{t(getCancelReasonKey(item.cancelReason))}
							</Badge>
						)}
					</div>
				)}
				renderEntityIcon={item => <ParkingSessionEntityIcon status={item.status} />}
				onEdit={id => {
					// Only allow editing if the session is still inside
					const session = filteredParkingSessions.find(
						s => s.parkingSessionId === id
					);
					if (session?.status === ParkingSessionStatus.Inside) {
						go({ to: `/parking-sessions/edit/${id}` });
					} else {
						toast.info(t('messages.notEditable'));
					}
				}}

				showCreateButton={true}
				onCreate={() => go({ to: '/parking-sessions/create' })}
				renderCustomFilters={() => (
					<div className="flex gap-2">
						<div className="w-44">
							<SearchInput
								value={searchText}
								onChange={e => setSearchText(e.target.value)}
								placeholder={t('list.searchPlaceholder')}
								variant="transparent"
								className="h-8 text-xs"
							/>
						</div>
						<div className="w-36">
							<Input
								type="date"
								value={selectedDate}
								onChange={e => setSelectedDate(e.target.value)}
								variant="transparent"
								className="h-8 text-xs"
							/>
						</div>
						<div className="w-36">
							<SentryDropdown
								options={statusOptions}
								value={selectedStatus}
								onChange={value =>
									setSelectedStatus(value as string)
								}
								placeholder={tPS('list.filters.all')}
								groupByCategory={false}
								className="w-full h-8 text-xs"
								variant="transparent"
							/>
						</div>
						{hasActiveFilters && (
							<Button
								variant="outline"
								size="sm"
								onClick={handleClearFilters}
								className="h-8 text-xs"
							>
								{t('list.clearFilters')}
							</Button>
						)}
					</div>
				)}
			/>
		</>
	);
};
